import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon/Icon';
import type { ToastAction } from './ToastContext';
import styles from './Toast.module.scss';

export type ToastVariant = 'success' | 'error';

interface Props {
  message: string;
  variant: ToastVariant;
  onClose: () => void;
  actions?: ToastAction[];
}

export function Toast({ message, variant, onClose, actions }: Props) {
  const { t } = useTranslation();
  const hasActions = !!actions && actions.length > 0;
  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      className={classNames(styles.toast, {
        [styles.success]: variant === 'success',
        [styles.error]: variant === 'error',
      })}
      data-testid="toast"
    >
      <Icon name={variant === 'success' ? 'check' : 'info'} classname={styles.icon} />
      <span className={styles.message}>{message}</span>
      {hasActions && (
        <div className={styles.actions}>
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={styles.actionButton}
              onClick={() => {
                action.onClick();
                onClose();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <button type="button" className={styles.close} onClick={onClose} aria-label={t('icons.close')}>
        <Icon name="close" />
      </button>
    </div>
  );
}
